/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { useMemo } from 'react'
import { gql } from '@faststore/graphql-utils'
import { createCartStore } from '@faststore/sdk'
import type { Cart as SDKCart, CartItem as SDKCartItem } from '@faststore/sdk'
import type {
  CartItemFragment,
  CartMessageFragment,
  IStoreOffer,
  ValidateCartMutationMutation,
  ValidateCartMutationMutationVariables,
} from '@generated/graphql'

import { request } from '../graphql/request'
import { sessionStore } from '../session'
import { createValidationStore, useStore } from '../useStore'

export interface CartItem extends SDKCartItem, Omit<CartItemFragment, 'price'> {
  price: number
  breadcrumbList: {
    itemListElement: Array<{ name: string; }>
  }
  slug: string
}

export interface UseCartReturn extends Cart {
  isValidating: boolean
  messages: CartMessageFragment[] | undefined
  gifts: CartItem[]
  items: CartItem[]
  totalUniqueItems: number
  totalItems: number
  total: number
  subTotal: number
  id: string
}

export interface Cart extends SDKCart<CartItem> {
  messages?: CartMessageFragment[]
  shouldSplitItem?: boolean
}

export const ValidateCartMutation = gql`
  mutation ValidateCartMutation($cart: IStoreCart!, $session: IStoreSession!) {
    validateCart(cart: $cart, session: $session) {
      order {
        orderNumber
        acceptedOffer {
          ...CartItem
        }
      }
      messages {
        ...CartMessage
      }
    }
  }
  fragment CartMessage on StoreCartMessage {
    text
    status
  }
  fragment CartItem on StoreOffer {
    seller {
      identifier
    }
    quantity
    price
    listPrice
    itemOffered {
      ...CartProductItem
      gtin
      slug
    }
  }
  fragment CartProductItem on StoreProduct {
    sku
    name
    slug
    image {
      url
      alternateName
    }
    brand {
      name
    }
    isVariantOf {
      productGroupID
      name
      additionalProperty {
        propertyID
        name
        value
        valueReference
      }
      hasVariant {
        description
        name
        sku
        gtin
        offers {
          lowPrice
          offers {
            availability
            listPrice
            price
          }
        }
        additionalProperty {
          propertyID
          name
          value
          valueReference
        }
      }
    }
    gtin
    additionalProperty {
      propertyID
      name
      value
      valueReference
    }
  }
`

const isGift = (item: CartItem) => item?.price === 0

const getItemId = (item: Pick<CartItem, 'itemOffered' | 'seller' | 'price'>) =>
  [item.itemOffered.sku, item.seller.identifier].filter(Boolean).join('::')

const [validationStore, onValidate] = createValidationStore(validateCart)
const defaultCartStore = createCartStore<Cart>(
  {
    id: '',
    items: [],
    messages: [],
  },
  onValidate
)

async function validateCart(cart: Cart): Promise<Cart | null> {
  if (defaultCartStore) {
    cart = {
      ...cart,
      items: (cart?.items || []).filter((item) => {
        const itemName = item.itemOffered?.name?.toLowerCase()

        if (
          itemName &&
          (itemName.includes('brinde') || itemName.includes('brindes')) &&
          item.price !== 0
        ) {
          defaultCartStore.removeItem(item.id)
          return false
        }

        return true
      }),
    }
  }

  const { validateCart: validated = null } = await request<
    ValidateCartMutationMutation,
    ValidateCartMutationMutationVariables
  >(ValidateCartMutation, {
    session: sessionStore.read(),
    cart: {
      order: {
        orderNumber: cart.id,
        shouldSplitItem: cart.shouldSplitItem as boolean,
        acceptedOffer: cart.items.map(
          ({
            price,
            listPrice,
            seller,
            quantity,
            itemOffered
          }): IStoreOffer => ({
            price,
            listPrice,
            seller,
            quantity,
            itemOffered: {
              sku: itemOffered.sku,
              image: itemOffered.image,
              name: itemOffered.name,
              additionalProperty: itemOffered.additionalProperty,
            },
          })
        ),
      },
    },
  })

  return (
    validated && {
      id: validated.order.orderNumber,
      items: validated.order?.acceptedOffer?.map((item) => ({
        ...item,
        id: getItemId(item),
      })),
      messages: validated.messages,
    }
  )
}

export const cartStore = {
  ...defaultCartStore,
  addItem: (item: Omit<CartItem, 'id'>) => {
    const cartItem = {
      ...item,
      id: getItemId(item),
    }

    defaultCartStore.addItem(cartItem)
  },
}

export const useCart = (): UseCartReturn => {
  const cart = useStore(cartStore)
  const isValidating = useStore(validationStore)

  return useMemo(
    () => ({
      ...cart,
      isValidating,
      messages: cart.messages,
      gifts: cart.items?.filter((item) => isGift(item)),
      items: cart.items,
      totalUniqueItems: cart.items.length,
      totalItems: cart.items?.reduce(
        (acc, curr) => acc + curr.quantity,
        0
      ),
      total: cart.items?.reduce(
        (acc, curr) => acc + curr?.price * curr?.quantity,
        0
      ),
      subTotal: cart.items?.reduce(
        (acc, curr) => acc + curr?.listPrice * curr?.quantity,
        0
      ),
    }),
    [cart, isValidating]
  )
}
